import React, { useEffect, useRef, useState } from 'react';
import { T, serif, Boton, Aviso } from '../lib/ui';
import { escanearConCamara, leerDeArchivo, prepararFoto, hayDetectorNativo } from '../lib/qr';

// ════════════════════════════════════════════════════════════════════
// Lector del QR de comprobantes electronicos
//
// Dos caminos, porque el equipo usa Android y iPhone:
//
//   BarcodeDetector  viene en Chrome y Edge sobre Android. Rapido y
//                    sin descargas.
//   jsQR             respaldo para Safari en iPhone, que NO trae
//                    BarcodeDetector. Se descarga solo cuando hace
//                    falta, asi que en Android no pesa nada.
//
// Si la camara no coopera, queda la salida de leer el QR desde una
// foto ya tomada.
// ════════════════════════════════════════════════════════════════════

export function LectorQr({ onLeido, onCerrar, onLeerDocumento, lecturaFotoDisponible }) {
  const videoRef = useRef(null);
  const escaneoRef = useRef(null);
  const archivoRef = useRef(null);
  const documentoRef = useRef(null);
  const [error, setError] = useState('');
  const [estado, setEstado] = useState('Abriendo la cámara…');
  const [analizando, setAnalizando] = useState(false);

  useEffect(() => {
    if (!videoRef.current) return undefined;

    escaneoRef.current = escanearConCamara(videoRef.current, {
      alEstado: setEstado,
      alLeer: (texto) => onLeido(texto),
      alFallar: (msg) => setError(msg),
    });

    return () => escaneoRef.current?.detener();
  }, [onLeido]);

  const cerrar = () => {
    escaneoRef.current?.detener();
    onCerrar();
  };

  // Salida cuando la camara no logra enfocar: leer el QR de una foto.
  //
  // Si en esa foto tampoco hay QR legible, no se deja al usuario en un
  // callejon: se le ofrece leer el documento completo con la misma
  // foto que acaba de tomar.
  const desdeFoto = async (e) => {
    const archivo = e.target.files?.[0];
    if (!archivo) return;
    e.target.value = '';

    setAnalizando(true);
    setError('');
    try {
      const texto = await leerDeArchivo(archivo);
      if (texto) {
        escaneoRef.current?.detener();
        onLeido(texto);
      } else if (onLeerDocumento) {
        // No hay QR, pero la foto sirve igual: se pasa al otro camino
        escaneoRef.current?.detener();
        onLeerDocumento(archivo);
      } else {
        setError('No se encontró un código QR en esa foto. Debe verse completo y sin reflejos.');
      }
    } catch (err) {
      setError(err.message);
    }
    setAnalizando(false);
  };

  // Leer el documento sin pasar por el QR, con una foto nueva
  const leerDocumento = () => {
    escaneoRef.current?.detener();
    documentoRef.current?.click();
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(10,15,26,.93)',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: 20,
    }}>
      <div style={{
        fontFamily: serif, fontSize: 22, color: '#fff',
        marginBottom: 6, textAlign: 'center',
      }}>
        Apunta al código QR
      </div>
      <div style={{
        color: T.brass, fontSize: 13, marginBottom: 18,
        textAlign: 'center', maxWidth: 320,
      }}>
        Está impreso en la factura o boleta electrónica, normalmente abajo.
      </div>

      <div style={{
        position: 'relative', width: '100%', maxWidth: 340,
        aspectRatio: '1 / 1', borderRadius: 14, overflow: 'hidden',
        background: '#000',
      }}>
        <video
          ref={videoRef}
          muted
          playsInline
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        {/* Marco guia */}
        <div style={{
          position: 'absolute', inset: '12%',
          border: `3px solid ${T.brass}`, borderRadius: 10,
          pointerEvents: 'none',
        }} />
      </div>

      {analizando && (
        <div style={{ color: T.brass, fontSize: 13, marginTop: 14 }}>
          Analizando la foto…
        </div>
      )}

      {!error && (
        <div style={{
          marginTop: 14, fontSize: 13, color: '#C8D2E0', textAlign: 'center',
        }}>
          {estado}
        </div>
      )}

      {error && (
        <div style={{ marginTop: 16, width: '100%', maxWidth: 340 }}>
          <Aviso tipo="warn">
            {error}
            {onLeerDocumento && (
              <span> Si el código está borroso o roto, usa
              «leer el documento»: llena los campos desde la foto.</span>
            )}
          </Aviso>
        </div>
      )}

      <div style={{
        display: 'grid', gap: 9, marginTop: 20,
        width: '100%', maxWidth: 340,
      }}>
        <Boton variante="acento" onClick={() => archivoRef.current?.click()}>
          Leer de una foto guardada
        </Boton>

        {/* Cuando el QR está borroso o roto, esta es la salida: se lee
            el documento entero en lugar del código. */}
        {onLeerDocumento && (
          <Boton variante="suave" onClick={leerDocumento}>
            El QR no se lee · leer el documento o un PDF
          </Boton>
        )}

        <Boton variante="suave" onClick={cerrar}>
          Cancelar
        </Boton>
      </div>

      <input ref={archivoRef} type="file" accept="image/*"
             onChange={desdeFoto} style={{ display: 'none' }} />
      <input ref={documentoRef} type="file" accept="image/*,application/pdf"
             onChange={(e) => {
               const a = e.target.files?.[0];
               e.target.value = '';
               if (a) onLeerDocumento(a);
             }}
             style={{ display: 'none' }} />

      {!hayDetectorNativo() && (
        <div style={{
          color: T.muted, fontSize: 11, marginTop: 16,
          textAlign: 'center', maxWidth: 320,
        }}>
          En iPhone la lectura tarda un par de segundos más.
        </div>
      )}
    </div>
  );
}

/**
 * Prepara un archivo para subirlo como adjunto.
 *
 * Las imagenes se reducen y comprimen en el telefono: una foto de
 * camara pesa 3 o 4 MB y asi baja a unos 200 KB, sin que el
 * comprobante deje de leerse. Los PDF se suben tal cual.
 *
 * Devuelve { nombre, mimeType, bytes, contenidoBase64 }.
 */
export async function leerArchivo(archivo) {
  if (archivo.type && archivo.type.startsWith('image/')) {
    const f = await prepararFoto(archivo);
    return {
      nombre: f.nombre,
      mimeType: f.tipoMime,
      bytes: f.bytes,
      contenidoBase64: f.base64,
    };
  }

  return new Promise((resolver, rechazar) => {
    const lector = new FileReader();
    lector.onload = () => {
      const resultado = String(lector.result);
      resolver({
        nombre: archivo.name,
        mimeType: archivo.type || 'application/octet-stream',
        bytes: archivo.size,
        contenidoBase64: resultado.slice(resultado.indexOf(',') + 1),
      });
    };
    lector.onerror = () => rechazar(new Error('No se pudo leer el archivo'));
    lector.readAsDataURL(archivo);
  });
}
